import React, { useCallback, useMemo, useState } from "react";
import { PASSENGER_KEYS } from "../../PassengersData/PassengersData";
import { CVAC_PASSENGER_FORM_SUBMIT } from "../../../utils/customerChatPassengerForm";
import {
  ENGLISH_ONLY_PERSON_DETAILS_MESSAGE,
  getEnglishOnlyPersonNameError,
  isEnglishOnlyPersonName,
} from "../../../utils/passengers/englishOnlyPersonName";
import "./PassengerFormChatView.css";

/** Digits only → DD/MM/YYYY while typing (כמו בשאר טפסי האתר). */
function formatBirthDateDmyInput(raw) {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function emptyValuesForSpec(spec) {
  const out = {};
  (spec?.passengers || []).forEach((p) => {
    const row = { slot_id: p.slot_id };
    (p.fields || []).forEach((f) => {
      row[f.key] = "";
    });
    out[p.slot_id] = row;
  });
  return out;
}

function errorsBySlot(validationErrors) {
  const m = {};
  (validationErrors || []).forEach((e) => {
    if (!e?.slot_id) return;
    if (!m[e.slot_id]) m[e.slot_id] = {};
    if (e.field) m[e.slot_id][e.field] = e.message_he || "";
    else m[e.slot_id]._general = e.message_he || "";
  });
  return m;
}

const PassengerFormChatView = React.forwardRef(function PassengerFormChatView(
  { spec, disabled, sessionId, onSendControlTurn, validationErrors },
  ref
) {
  const [valuesBySlot, setValuesBySlot] = useState(() => emptyValuesForSpec(spec));
  const [localErrors, setLocalErrors] = useState({});

  React.useEffect(() => {
    setValuesBySlot(emptyValuesForSpec(spec));
    setLocalErrors({});
  }, [spec]);

  const serverErr = useMemo(
    () => errorsBySlot(validationErrors || spec?.validation_errors),
    [validationErrors, spec]
  );

  const setField = useCallback((slotId, key, v) => {
    setValuesBySlot((prev) => ({
      ...prev,
      [slotId]: { ...prev[slotId], [key]: v },
    }));
  }, []);

  /** מייל איש קשר ראשי → כל שאר הנוסעים עם שדה email (אוטומטית). */
  const setPrimaryEmailMirrored = useCallback(
    (slotId, v) => {
      setValuesBySlot((prev) => {
        const pax = spec?.passengers || [];
        const editor = pax.find((x) => x.slot_id === slotId);
        const next = { ...prev };
        next[slotId] = { ...(prev[slotId] || {}), [PASSENGER_KEYS.EMAIL]: v };
        if (editor?.is_primary_contact) {
          pax.forEach((p) => {
            if (p.slot_id === slotId) return;
            const hasEmail = (p.fields || []).some((f) => f.key === PASSENGER_KEYS.EMAIL);
            if (hasEmail) {
              next[p.slot_id] = {
                ...(prev[p.slot_id] || {}),
                [PASSENGER_KEYS.EMAIL]: v,
              };
            }
          });
        }
        return next;
      });
    },
    [spec]
  );

  const validateClient = useCallback(() => {
    const errs = {};
    const dmy = (s) => /^\d{2}\/\d{2}\/\d{4}$/.test(String(s || "").trim());
    const emailOk = (s) => {
      const t = String(s || "").trim();
      return t.length >= 3 && t.includes("@") && t.split("@").pop().includes(".");
    };
    const cellOk = (s) => {
      const t = String(s || "").replace(/\s/g, "");
      return /^\+?[0-9-]{9,20}$/.test(t);
    };

    (spec?.passengers || []).forEach((p) => {
      const row = valuesBySlot[p.slot_id] || {};
      const slotErr = {};
      (p.fields || []).forEach((f) => {
        const raw = row[f.key];
        const t = String(raw ?? "").trim();
        if (f.required && !t) {
          slotErr[f.key] = "שדה חובה";
          return;
        }
        if (!t) return;
        if (f.ascii_letters && !isEnglishOnlyPersonName(t)) {
          slotErr[f.key] = ENGLISH_ONLY_PERSON_DETAILS_MESSAGE;
        }
        if (f.type === "date_dmy" && !dmy(t)) {
          slotErr[f.key] = "פורמט DD/MM/YYYY";
        }
        if (f.key === PASSENGER_KEYS.EMAIL && t && !emailOk(t)) {
          slotErr[f.key] = "דוא\"ל לא תקין";
        }
        if (f.key === PASSENGER_KEYS.CELL && t && !cellOk(t)) {
          slotErr[f.key] = "טלפון לא תקין";
        }
      });
      if (Object.keys(slotErr).length) errs[p.slot_id] = slotErr;
    });
    return errs;
  }, [spec, valuesBySlot]);

  const onSubmit = useCallback(() => {
    const ce = validateClient();
    setLocalErrors(ce);
    if (Object.keys(ce).length) return;
    const values = (spec?.passengers || []).map((p) => ({
      ...(valuesBySlot[p.slot_id] || {}),
      slot_id: p.slot_id,
    }));
    onSendControlTurn?.({
      messageText: CVAC_PASSENGER_FORM_SUBMIT,
      metadata: { cvac_passenger_form_submit: { version: 1, values } },
      userDisplayText: "אישור פרטי נוסעים",
    });
  }, [onSendControlTurn, spec, validateClient, valuesBySlot]);

  React.useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        onSubmit();
      },
    }),
    [onSubmit]
  );

  if (!spec?.passengers?.length) return null;

  return (
    <div className="hr-cvac-pax" dir="rtl">
      <div className="hr-cvac-pax__head">פרטי נוסעים</div>
      {(spec.passengers || []).map((p) => {
        const row = valuesBySlot[p.slot_id] || {};
        const le = localErrors[p.slot_id] || {};
        const se = serverErr[p.slot_id] || {};
        return (
          <div key={p.slot_id} className="hr-cvac-pax__block">
            <div className="hr-cvac-pax__role">
              {p.role_he}
              {p.is_primary_contact ? (
                <span className="hr-cvac-pax__badge">איש קשר ראשי</span>
              ) : null}
            </div>
            {(p.fields || []).map((f) => {
              const liveNameErr =
                f.ascii_letters
                  ? getEnglishOnlyPersonNameError(row[f.key])
                  : null;
              const err = liveNameErr || le[f.key] || se[f.key] || "";
              const requiredStar = f.required ? (
                <span className="hr-cvac-pax__req" aria-hidden>
                  {" "}
                  *
                </span>
              ) : null;
              if (f.type === "title" || f.type === "gender") {
                return (
                  <label key={f.key} className="hr-cvac-pax__field">
                    <span className="hr-cvac-pax__label">
                      {f.label_he}
                      {requiredStar}
                    </span>
                    <select
                      className="hr-cvac-pax__input"
                      value={row[f.key] || ""}
                      disabled={disabled}
                      onChange={(e) => setField(p.slot_id, f.key, e.target.value)}
                    >
                      <option value="">בחרו…</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {err ? <span className="hr-cvac-pax__err">{err}</span> : null}
                  </label>
                );
              }
              if (f.type === "date_dmy") {
                return (
                  <label key={f.key} className="hr-cvac-pax__field">
                    <span className="hr-cvac-pax__label">
                      {f.label_he}
                      {requiredStar}
                    </span>
                    <input
                      type="text"
                      dir="ltr"
                      inputMode="numeric"
                      autoComplete="bday"
                      placeholder={f.placeholder || "DD/MM/YYYY"}
                      className="hr-cvac-pax__input"
                      value={row[f.key] || ""}
                      disabled={disabled}
                      onChange={(e) =>
                        setField(p.slot_id, f.key, formatBirthDateDmyInput(e.target.value))
                      }
                    />
                    {err ? <span className="hr-cvac-pax__err">{err}</span> : null}
                  </label>
                );
              }
              return (
                <label key={f.key} className="hr-cvac-pax__field">
                  <span className="hr-cvac-pax__label">
                    {f.label_he}
                    {requiredStar}
                  </span>
                  <input
                    type={f.key === PASSENGER_KEYS.EMAIL ? "email" : "text"}
                    className="hr-cvac-pax__input"
                    value={row[f.key] || ""}
                    disabled={disabled}
                    onChange={(e) =>
                      f.key === PASSENGER_KEYS.EMAIL && p.is_primary_contact
                        ? setPrimaryEmailMirrored(p.slot_id, e.target.value)
                        : setField(p.slot_id, f.key, e.target.value)
                    }
                    autoComplete={
                      f.key === PASSENGER_KEYS.EMAIL
                        ? "email"
                        : f.key === PASSENGER_KEYS.CELL
                          ? "tel"
                          : "off"
                    }
                  />
                  {err ? <span className="hr-cvac-pax__err">{err}</span> : null}
                </label>
              );
            })}
          </div>
        );
      })}
      <button
        type="button"
        className="hr-cvac-pax__submit"
        disabled={disabled || !sessionId}
        onClick={onSubmit}
      >
        {spec.continue_label_he || "המשך"}
      </button>
    </div>
  );
});

export default PassengerFormChatView;
